/**
 * useMeetingPrepSearch Hook
 * TASK-293: Orchestrates meeting prep search using SOLID utilities
 *
 * Replaces useDataGathering with search service integration
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type { Insight, DataSourceType } from '../../types/dataGathering';
import {
  calculateDateRange,
  buildSearchContext,
  buildSearchQueries,
  executeSearches,
  executeSourceSearch,
  aggregateResults,
  classifyResults,
  sortByRelevance,
  importanceRank,
  isSearchComplete,
  hasSearchError,
  validateSearchOptions,
  handleError,
  mapSourceType,
} from '../../utils/meetingPrep';
import type { SearchInsight, SourceType } from '../../types/search.types';
import { insightService } from '../../services/insightService';
import type {
  UseMeetingPrepSearchOptions,
  UseMeetingPrepSearchReturn,
  MeetingPrepSource,
  MeetingPrepSourceType,
  DateRangeInfo,
  ClassifiedResult,
  SearchQueries,
  SearchContext,
} from '../../utils/meetingPrep/types';

/**
 * Hook for meeting preparation search
 * Orchestrates the search flow using SOLID utilities
 */
export function useMeetingPrepSearch(
  options: UseMeetingPrepSearchOptions
): UseMeetingPrepSearchReturn {
  const { clientId, clientName, meetingDate, autoStart = true } = options;

  // State
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isExtractingInsights, setIsExtractingInsights] = useState(false);
  const [sources, setSources] = useState<MeetingPrepSource[]>([]);
  const [dateRange, setDateRange] = useState<DateRangeInfo | null>(null);
  const [mustDiscuss, setMustDiscuss] = useState<ClassifiedResult[]>([]);
  const [shouldReview, setShouldReview] = useState<ClassifiedResult[]>([]);
  const [context, setContext] = useState<ClassifiedResult[]>([]);
  const [summary, setSummary] = useState('');
  const [insights, setInsights] = useState<Insight[]>([]);

  // Refs for cancellation and caching
  const abortControllerRef = useRef<AbortController | null>(null);
  const searchContextRef = useRef<SearchContext | null>(null);
  const queriesRef = useRef<SearchQueries | null>(null);
  const hasStartedRef = useRef(false);

  /**
   * Cancel ongoing searches
   */
  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  /**
   * Handle source update callback
   */
  const handleSourceUpdate = useCallback((updatedSource: MeetingPrepSource) => {
    setSources((prev) =>
      prev.map((s) => (s.type === updatedSource.type ? updatedSource : s))
    );
  }, []);

  /**
   * Parse source ID to extract type and ID
   * Format: "source_type:source_id" (e.g., "email:email_search-result", "database:client-123")
   */
  const parseSourceId = (sourceId: string): { type: DataSourceType; id: string } => {
    const [typeStr, ...idParts] = sourceId.split(':');
    const id = idParts.join(':') || sourceId;

    // Map backend source types to frontend DataSourceType (shared mapping, unknown → 'notes')
    return {
      type: mapSourceType(typeStr.toLowerCase() as SourceType),
      id,
    };
  };

  /**
   * Convert SearchInsight from API to Insight format for UI
   */
  const convertToInsight = (searchInsight: SearchInsight, index: number): Insight => {
    // Map API category to UI category (dataGathering.ts InsightCategory)
    // UI categories: 'positive' | 'neutral' | 'alert' | 'action_needed'
    const categoryMap: Record<string, 'positive' | 'neutral' | 'alert' | 'action_needed'> = {
      action_needed: 'action_needed',
      opportunity: 'positive',
      risk: 'alert',
      life_event: 'neutral',
      portfolio: 'positive',
      general: 'neutral',
    };

    return {
      id: `insight-${index}`,
      title: searchInsight.title,
      description: searchInsight.description,
      category: categoryMap[searchInsight.category] || 'neutral',
      confidence: searchInsight.confidence,
      sources: searchInsight.sourceIds.map((sourceId) => {
        const parsed = parseSourceId(sourceId);
        return {
          type: parsed.type,
          id: parsed.id,
          title: getSourceTitle(parsed.type),
          date: new Date().toISOString(),
          excerpt: '',
          relevance: searchInsight.confidence,
        };
      }),
    };
  };

  /**
   * Get human-readable title for source type
   */
  const getSourceTitle = (type: DataSourceType): string => {
    const titles: Record<DataSourceType, string> = {
      email: 'Email',
      notes: 'Client Notes',
      calendar: 'Calendar',
      news: 'News',
      meeting_notes: 'Meeting Notes',
      life_events: 'Life Events',
      market_news: 'Market Data',
    };
    return titles[type] || 'Source';
  };

  /**
   * Process results after search completes
   * Calls insight extraction service separately (TASK-XXX: SOLID refactor)
   */
  const processResults = useCallback(async (
    searchSources: MeetingPrepSource[],
    signal?: AbortSignal
  ) => {
    // Aggregate all results
    const aggregated = aggregateResults(searchSources);

    // Classify by importance (for legacy classified results)
    const classified = classifyResults(aggregated.allSources);
    const sorted = sortByRelevance(classified);

    setMustDiscuss(sorted.mustDiscuss);
    setShouldReview(sorted.shouldReview);
    setContext(sorted.context);

    // Build results text and source IDs from search responses
    // This is passed to the insight extraction service
    const resultsText = searchSources
      .filter((s) => s.data?.answer)
      .map((s) => `[${s.type.toUpperCase()}]\n${s.data!.answer}`)
      .join('\n\n');

    const sourceIds = searchSources
      .filter((s) => s.data?.sources)
      .flatMap((s) =>
        s.data!.sources.map((src) => `${src.sourceType}:${src.sourceId}`)
      );

    // Get the main query from refs (use client query as representative)
    const query = queriesRef.current?.client || `Meeting preparation for ${clientName}`;

    // Extract insights from Meeting Orchestrator (separate from search)
    let allInsights: SearchInsight[] = [];
    setIsExtractingInsights(true);
    try {
      const insightResult = await insightService.extractInsights(
        {
          query,
          resultsText,
          sourceIds,
          maxInsights: 10,
        },
        signal
      );

      if (insightResult.success && insightResult.insights.length > 0) {
        allInsights = insightResult.insights;
      }
    } catch (error) {
      // Graceful degradation - continue without insights
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('[useMeetingPrepSearch] Insight extraction failed:', error);
      }
    } finally {
      setIsExtractingInsights(false);
    }

    // Convert API insights to UI format and deduplicate by title
    const seenTitles = new Set<string>();
    const uniqueInsights: Insight[] = [];
    for (let i = 0; i < allInsights.length && uniqueInsights.length < 10; i++) {
      const insight = allInsights[i];
      const titleKey = insight.title.toLowerCase().trim();
      if (!seenTitles.has(titleKey)) {
        seenTitles.add(titleKey);
        uniqueInsights.push(convertToInsight(insight, uniqueInsights.length));
      }
    }

    // Sort by importance (MUST_DISCUSS first, then SHOULD_REVIEW, then CONTEXT)
    uniqueInsights.sort((a, b) => {
      const aImportance = allInsights.find((i) => i.title === a.title)?.importance ?? 'CONTEXT';
      const bImportance = allInsights.find((i) => i.title === b.title)?.importance ?? 'CONTEXT';
      return importanceRank(aImportance) - importanceRank(bImportance);
    });

    setInsights(uniqueInsights);

    // Generate summary based on insights
    const completedSources = searchSources.filter((s) => s.status === 'completed');
    const actionItems = uniqueInsights.filter((i) => i.category === 'action_needed').length;
    const alertItems = uniqueInsights.filter((i) => i.category === 'alert').length;
    const positiveItems = uniqueInsights.filter((i) => i.category === 'positive').length;

    let summaryText = 'Data gathering completed successfully.';
    if (uniqueInsights.length > 0) {
      const parts: string[] = [];
      if (actionItems > 0) parts.push(`${actionItems} action item${actionItems > 1 ? 's' : ''}`);
      if (alertItems > 0) parts.push(`${alertItems} alert${alertItems > 1 ? 's' : ''}`);
      if (positiveItems > 0) parts.push(`${positiveItems} positive finding${positiveItems > 1 ? 's' : ''}`);
      if (parts.length > 0) {
        summaryText = `Found ${parts.join(', ')} across ${completedSources.length} sources.`;
      } else {
        summaryText = `Found ${uniqueInsights.length} insight${uniqueInsights.length > 1 ? 's' : ''} from ${completedSources.length} sources.`;
      }
    } else {
      summaryText = `Searched ${completedSources.length} sources. No insights found.`;
    }
    setSummary(summaryText);
  }, [clientName]);

  /**
   * Start the search process
   */
  const startSearch = useCallback(async () => {
    // Validate client context and options before doing any work
    const validation = validateSearchOptions(options);
    if (!validation.valid) {
      console.error('[useMeetingPrepSearch] Invalid options:', validation.error);
      setHasError(true);
      return;
    }

    // Cancel any existing searches
    cancel();

    // Create new abort controller
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setIsLoading(true);
    setIsComplete(false);
    setHasError(false);

    // Initialize sources as pending
    // TODO: 'calendar' and 'portfolio' scaffolded — backend search tools needed
    const initialSources: MeetingPrepSource[] = [
      { type: 'client', status: 'pending', data: null, itemCount: 0 },
      { type: 'email', status: 'pending', data: null, itemCount: 0 },
      { type: 'market', status: 'pending', data: null, itemCount: 0 },
      { type: 'news', status: 'pending', data: null, itemCount: 0 },
      { type: 'documents', status: 'pending', data: null, itemCount: 0 },
      { type: 'calendar', status: 'pending', data: null, itemCount: 0 },
      { type: 'portfolio', status: 'pending', data: null, itemCount: 0 },
    ];
    setSources(initialSources);

    try {
      // Step 1: Calculate date range
      const calculatedDateRange = await calculateDateRange(clientId, meetingDate);
      setDateRange(calculatedDateRange);

      if (signal.aborted) return;

      // Step 2: Build search context
      const searchContext = await buildSearchContext(clientId, clientName);
      searchContextRef.current = searchContext;

      if (signal.aborted) return;

      // Step 3: Build queries
      const queries = buildSearchQueries(searchContext, calculatedDateRange);
      queriesRef.current = queries;

      // Step 4: Execute searches in parallel (TASK-400: pass clientId for fast-path routing)
      const searchResults = await executeSearches(
        queries,
        calculatedDateRange,
        signal,
        undefined,
        handleSourceUpdate,
        clientId,
      );

      if (signal.aborted) return;

      // Step 5: Update state
      setSources(searchResults);
      setIsComplete(isSearchComplete(searchResults));
      setHasError(hasSearchError(searchResults));

      // Step 6: Process results and extract insights
      await processResults(searchResults, signal);
    } catch (error: any) {
      // handleError returns { action: 'ignore' } for AbortError
      if (handleError(error).action !== 'ignore') {
        console.error('[useMeetingPrepSearch] Search failed:', error);
        setHasError(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, [options, clientId, clientName, meetingDate, cancel, handleSourceUpdate, processResults]);

  /**
   * Refresh a single source
   */
  const refreshSource = useCallback(
    async (sourceType: MeetingPrepSourceType) => {
      if (!dateRange || !queriesRef.current) {
        return;
      }

      // Update source to loading
      setSources((prev) =>
        prev.map((s) => (s.type === sourceType ? { ...s, status: 'loading' as const } : s))
      );

      try {
        const result = await executeSourceSearch(
          sourceType,
          queriesRef.current[sourceType],
          dateRange,
          abortControllerRef.current?.signal,
          undefined,
          clientId,
        );

        // Update sources with the new result
        const updatedSources = sources.map((s) =>
          s.type === sourceType ? result : s
        );
        setSources(updatedSources);

        // Reprocess all results (async insight extraction)
        await processResults(updatedSources, abortControllerRef.current?.signal);
      } catch (error) {
        if (handleError(error as Error).action !== 'ignore') {
          console.error(`[useMeetingPrepSearch] Failed to refresh ${sourceType}:`, error);
        }
      }
    },
    [dateRange, sources, processResults]
  );

  /**
   * Refresh all sources
   */
  const refreshAll = useCallback(async () => {
    await startSearch();
  }, [startSearch]);

  // Reset hasStarted when clientId changes
  useEffect(() => {
    hasStartedRef.current = false;
  }, [clientId]);

  // Auto-start on mount if enabled (use ref to prevent re-running)
  useEffect(() => {
    if (autoStart && clientId && clientName && !hasStartedRef.current) {
      hasStartedRef.current = true;
      startSearch();
    }

    // Cleanup on unmount
    return () => {
      cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, clientId, clientName]);

  // Legacy format accessors
  const communications = sources
    .find((s) => s.type === 'email')
    ?.data?.sources || [];

  const marketData = sources
    .find((s) => s.type === 'market')
    ?.data?.sources || [];

  const newsItems = sources
    .find((s) => s.type === 'news')
    ?.data?.sources || [];

  const documents = sources
    .find((s) => s.type === 'documents')
    ?.data?.sources || [];

  return {
    // State
    isLoading,
    isComplete,
    hasError,
    isExtractingInsights,
    sources,
    dateRange,

    // Classified results
    mustDiscuss,
    shouldReview,
    context,

    // Legacy format
    communications,
    marketData,
    newsItems,
    documents,
    insights,
    summary,

    // Actions
    startSearch,
    refreshSource,
    refreshAll,
    cancel,
  };
}

export default useMeetingPrepSearch;
